import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Job, Lead, JobStatus, Contact } from '../types';
import { Icons } from '../constants';
import MapComponent from './MapComponent';
import { db, auth } from '../firebase';
import { collection, addDoc, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { apiUrl } from '../api';
import { handleFirestoreError, isMissingDocumentError, OperationType } from '../lib/firestore-errors';
import { logChange, exportCsv } from '../lib/changeLog';
import { 
  Plus, 
  Edit3, 
  Trash2, 
  Building, 
  FileText, 
  Phone, 
  Mail, 
  MapPin, 
  Search, 
  Filter, 
  X, 
  Users, 
  Briefcase, 
  TrendingUp, 
  AlertCircle 
} from 'lucide-react';

interface Props {
  jobs: Job[];
  leads: Lead[];
  contacts?: Contact[];
  googleConnected?: boolean;
  onImportContacts?: () => void;
  onConnectGoogle?: () => void;
  actingUser?: string;
}

interface ClientItem {
  id?: string; // Contact document ID if exists
  name: string;
  email: string;
  phone: string;
  address: string;
  companyName: string;
  notes: string;
  personType?: string;
  jobs: Job[];
  leads: Lead[];
  totalRevenue: number;
  lastActivity: Date | null;
}

const ClientsView: React.FC<Props> = ({ 
  jobs, 
  leads, 
  contacts = [], 
  googleConnected, 
  onImportContacts, 
  onConnectGoogle,
  actingUser 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState<'ALL' | 'CORPORATE' | 'ACTIVE' | 'INACTIVE'>('ALL');
  
  // Reactive selected client identifier
  const [selectedClientEmail, setSelectedClientEmail] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Form State
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<ClientItem | null>(null);
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [formAddress, setFormAddress] = useState('');
  const [formCompanyName, setFormCompanyName] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [formPersonType, setFormPersonType] = useState<'CUSTOMER' | 'EMPLOYEE' | 'CONTRACTOR' | 'OTHER'>('CUSTOMER');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Email state
  const [isComposingEmail, setIsComposingEmail] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [isSending, setIsSending] = useState(false);

  const handleSendEmail = async () => {
    if (!selectedClient || !emailSubject || !emailBody) return;
    setIsSending(true);
    try {
      const response = await fetch(apiUrl('/api/gmail/send'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: selectedClient.email,
          subject: emailSubject,
          body: emailBody
        })
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to send email');
      }
      
      alert('Email sent successfully!');
      setIsComposingEmail(false);
      setEmailSubject('');
      setEmailBody('');
    } catch (error: any) {
      alert(`Error sending email: ${error.message}`);
    } finally {
      setIsSending(false);
    }
  };

  // Compile unique clients list by merging jobs, leads, and custom contacts
  const clients = useMemo(() => {
    const clientMap = new Map<string, ClientItem>();
    // A deleted contact should hide the person even when jobs or leads with the
    // same email/name still exist. Otherwise the row is rebuilt from those
    // records immediately after deletion and looks like the delete did nothing.
    const deletedKeys = new Set<string>();
    for (const c of contacts) {
      if (!(c as any).isDeleted) continue;
      if (c.email) deletedKeys.add(c.email.toLowerCase());
      if (c.name) deletedKeys.add(c.name.toLowerCase());
    }

    const processItem = (item: Lead | Job, isJob: boolean) => {
      if (!item.email && !item.clientName) return;
      if ((item as any).isDeleted) return;
      if ((item.email && deletedKeys.has(item.email.toLowerCase())) ||
          (item.clientName && deletedKeys.has(item.clientName.toLowerCase()))) return;
      const key = (item.email || item.clientName).toLowerCase();
      if (!clientMap.has(key)) {
        clientMap.set(key, {
          name: item.clientName,
          email: item.email || '',
          phone: item.phone || '',
          address: item.address || '',
          companyName: '',
          notes: '',
          jobs: [],
          leads: [],
          totalRevenue: 0,
          lastActivity: null
        });
      }
      const client = clientMap.get(key)!;
      
      const itemDate = new Date(item.createdAt);
      if (!client.lastActivity || itemDate > client.lastActivity) {
        client.lastActivity = itemDate;
      }

      if (isJob) {
        const job = item as Job;
        client.jobs.push(job);
        client.totalRevenue += job.totalPrice || 0;
      } else {
        client.leads.push(item as Lead);
      }
    };

    const processContact = (contact: Contact) => {
      if (!contact.email && !contact.name) return;
      if ((contact as any).isDeleted) return;
      // Staff, contractors and address-book imports are not customers. They stay out of this
      // list unless the owner searches for them, which is how one gets found and reclassified.
      if (!searchTerm && contact.personType && contact.personType !== 'CUSTOMER') return;
      const key = (contact.email || contact.name).toLowerCase();
      if (!clientMap.has(key)) {
        clientMap.set(key, {
          id: contact.id,
          name: contact.name,
          email: contact.email || '',
          phone: contact.phone || '',
          address: contact.address || '',
          companyName: contact.companyName || '',
          notes: contact.notes || '',
          personType: contact.personType || 'CUSTOMER',
          jobs: [],
          leads: [],
          totalRevenue: 0,
          lastActivity: new Date(contact.createdAt || Date.now())
        });
      } else {
        const existing = clientMap.get(key)!;
        existing.id = contact.id;
        // The contact record is the customer's canonical profile and is what the edit form writes
        // to, so its values must win over those inherited from a job or lead. Name and email were
        // previously omitted here, which made every name/email edit appear to do nothing.
        if (contact.name) existing.name = contact.name;
        if (contact.email) existing.email = contact.email;
        if (contact.companyName) existing.companyName = contact.companyName;
        if (contact.notes) existing.notes = contact.notes;
        if (contact.phone) existing.phone = contact.phone;
        if (contact.address) existing.address = contact.address;
        if (contact.personType) existing.personType = contact.personType;
      }
    };

    leads.forEach(l => processItem(l, false));
    jobs.forEach(j => processItem(j, true));
    contacts.forEach(processContact);

    return Array.from(clientMap.values()).sort((a, b) => {
      if (!a.lastActivity) return 1;
      if (!b.lastActivity) return -1;
      return b.lastActivity.getTime() - a.lastActivity.getTime();
    });
  }, [jobs, leads, contacts, searchTerm]);

  // Efficient searching and filtering of customer records
  const filteredClients = useMemo(() => {
    return clients.filter(c => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        c.name.toLowerCase().includes(searchLower) ||
        c.email.toLowerCase().includes(searchLower) ||
        c.phone.toLowerCase().includes(searchLower) ||
        c.address.toLowerCase().includes(searchLower) ||
        c.companyName.toLowerCase().includes(searchLower) ||
        c.notes.toLowerCase().includes(searchLower);

      if (!matchesSearch) return false;

      if (activeFilter === 'CORPORATE') {
        return !!c.companyName;
      }
      if (activeFilter === 'ACTIVE') {
        return c.jobs.some(j => j.status !== JobStatus.PAID);
      }
      if (activeFilter === 'INACTIVE') {
        return c.jobs.length === 0 || c.jobs.every(j => j.status === JobStatus.PAID);
      }
      return true;
    });
  }, [clients, searchTerm, activeFilter]);

  const selectedClient = useMemo(() => {
    if (!selectedClientEmail) return null;
    return filteredClients.find(c => c.email.toLowerCase() === selectedClientEmail.toLowerCase()) || null;
  }, [filteredClients, selectedClientEmail]);

  const totalRevenue = clients.reduce((sum, client) => sum + client.totalRevenue, 0);
  const activeClientsCount = clients.filter(c => c.jobs.some(j => j.status !== JobStatus.PAID)).length;
  const corporateClientsCount = clients.filter(c => !!c.companyName).length;

  const resetForm = () => {
    setEditingClient(null);
    setFormName('');
    setFormEmail('');
    setFormPhone('');
    setFormAddress('');
    setFormCompanyName('');
    setFormNotes('');
    setFormPersonType('CUSTOMER');
  };

  const handleOpenAddForm = () => {
    resetForm();
    setIsFormOpen(true);
  };

  const handleOpenEditForm = (client: ClientItem) => {
    setEditingClient(client);
    setFormName(client.name);
    setFormEmail(client.email);
    setFormPhone(client.phone);
    setFormAddress(client.address);
    setFormCompanyName(client.companyName);
    setFormNotes(client.notes);
    setFormPersonType((client.personType as any) || 'CUSTOMER');
    setIsFormOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName.trim() || !formEmail.trim()) {
      alert('Contact Name and Email Address are required.');
      return;
    }

    setIsSubmitting(true);
    try {
      if (editingClient && editingClient.id) {
        // Update existing Firestore contact record
        const contactRef = doc(db, 'contacts', editingClient.id);
        await updateDoc(contactRef, {
          name: formName,
          email: formEmail,
          phone: formPhone,
          address: formAddress,
          companyName: formCompanyName,
          notes: formNotes,
          personType: formPersonType,
        });
        logChange({ who: actingUser || 'System', action: 'UPDATE', entity: 'contact', entityId: editingClient.id, entityLabel: formName, summary: `Customer updated: ${formName}`, changedFields: ['name', 'email', 'phone', 'address', 'companyName', 'notes'] });
      } else {
        // Create a new Firestore contact record
        const ref = await addDoc(collection(db, 'contacts'), {
          name: formName,
          email: formEmail,
          phone: formPhone,
          address: formAddress,
          companyName: formCompanyName,
          notes: formNotes,
          personType: formPersonType,
          createdAt: new Date().toISOString()
        });
        logChange({ who: actingUser || 'System', action: 'CREATE', entity: 'contact', entityId: ref.id, entityLabel: formName, summary: `Customer created: ${formName}` });
      }
      setIsFormOpen(false);
      resetForm();
    } catch (error: any) {
      console.error(error);
      handleFirestoreError(error, OperationType.WRITE, 'contacts');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (client: ClientItem) => {
    setDeleteError('');
    if (!client.id) {
      alert("This customer was built from a job or lead and has no contact record to delete. Delete the job from Operations instead, or add the person as a customer first.");
      return;
    }

    if (!window.confirm(`Are you sure you want to delete the customer record for "${client.name}"? This is a soft delete — the record is hidden but kept for audit and can be restored.`)) {
      return;
    }

    setIsDeleting(true);
    try {
      if (!auth.currentUser) throw new Error('Your session has expired. Please sign in again.');
      // Force a token refresh so a stale browser session gets a clear error
      // instead of a silent permission-denied on the write.
      await auth.currentUser.getIdToken(true);
      try {
        await updateDoc(doc(db, 'contacts', client.id), {
          isDeleted: true,
          deletedAt: new Date().toISOString(),
          deletedBy: actingUser || 'System',
        });
      } catch (error) {
        // Already deleted in another tab or purged server-side: the customer is
        // gone either way, so treat it as a successful delete.
        if (!isMissingDocumentError(error)) throw error;
      }
      logChange({ who: actingUser || 'System', action: 'DELETE', entity: 'contact', entityId: client.id, entityLabel: client.name, summary: `Customer deleted: ${client.name}` });
      setSelectedClientEmail(null);
    } catch (error: any) {
      console.error(error);
      const message = error?.code === 'permission-denied'
        ? 'Delete was blocked: your session expired or you do not have permission. Sign in again and retry.'
        : (error?.message || 'Could not delete this customer.');
      setDeleteError(message);
      alert(message);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header section with explicit responsive flow */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-black text-bark">Customer Directory</h2>
          <p className="text-stone-500 font-medium mt-1">Manage, edit, and filter your arboricultural clients.</p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto">
          {googleConnected && onImportContacts ? (
            <button
              onClick={onImportContacts}
              className="flex items-center justify-center space-x-2 px-4 py-3 bg-white text-bark border border-stone-200 rounded-2xl hover:bg-stone-50 transition shadow-sm font-bold text-sm"
            >
              <svg className="w-5 h-5 text-blue-500" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <span>Sync Contacts</span>
            </button>
          ) : onConnectGoogle ? (
             <button
              onClick={onConnectGoogle}
              className="flex items-center justify-center space-x-2 px-4 py-3 bg-white text-bark border border-stone-200 rounded-2xl hover:bg-stone-50 transition shadow-sm font-bold text-sm"
            >
              <svg className="w-5 h-5 text-blue-500" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <span>Connect Google</span>
            </button>
          ) : null}
          <button
            onClick={() => exportCsv(
              `customers-${new Date().toISOString().slice(0, 10)}.csv`,
              ['Name', 'Email', 'Phone', 'Address', 'Company', 'Total Revenue', 'Jobs', 'Leads', 'Notes'],
              filteredClients.map(c => [c.name, c.email, c.phone, c.address, c.companyName, c.totalRevenue, c.jobs.length, c.leads.length, c.notes])
            )}
            className="flex items-center justify-center space-x-2 px-5 py-3 bg-white text-bark border border-stone-200 rounded-2xl hover:bg-stone-50 transition shadow-sm font-bold text-sm"
          >
            <FileText size={18} />
            <span>Export CSV</span>
          </button>
          <button
            onClick={handleOpenAddForm}
            className="flex items-center justify-center space-x-2 px-5 py-3 bg-gecko hover:bg-gecko/90 text-white rounded-2xl shadow-lg shadow-gecko/20 transition font-black text-sm"
          >
            <Plus size={18} />
            <span>Add Customer</span>
          </button>
        </div>
      </div>

      {/* Enterprise Metrics Section */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-[32px] border border-stone-200 shadow-sm flex items-center space-x-4">
          <div className="w-14 h-14 bg-palm/10 rounded-2xl flex items-center justify-center text-palm">
            <Users size={24} />
          </div>
          <div>
            <p className="text-[10px] font-black text-stone-400 uppercase tracking-widest">Total Clients</p>
            <p className="text-2xl font-black text-bark">{clients.length}</p>
          </div>
        </div>
        <div className="bg-white p-6 rounded-[32px] border border-stone-200 shadow-sm flex items-center space-x-4">
          <div className="w-14 h-14 bg-gecko/10 rounded-2xl flex items-center justify-center text-gecko">
            <Briefcase size={24} />
          </div>
          <div>
            <p className="text-[10px] font-black text-stone-400 uppercase tracking-widest">Active Accounts</p>
            <p className="text-2xl font-black text-bark">{activeClientsCount}</p>
          </div>
        </div>
        <div className="bg-white p-6 rounded-[32px] border border-stone-200 shadow-sm flex items-center space-x-4">
          <div className="w-14 h-14 bg-amber-500/10 rounded-2xl flex items-center justify-center text-amber-600">
            <Building size={24} />
          </div>
          <div>
            <p className="text-[10px] font-black text-stone-400 uppercase tracking-widest">Corporate Accounts</p>
            <p className="text-2xl font-black text-bark">{corporateClientsCount}</p>
          </div>
        </div>
        <div className="bg-white p-6 rounded-[32px] border border-stone-200 shadow-sm flex items-center space-x-4">
          <div className="w-14 h-14 bg-gold/10 rounded-2xl flex items-center justify-center text-gold">
            <TrendingUp size={24} />
          </div>
          <div>
            <p className="text-[10px] font-black text-stone-400 uppercase tracking-widest">Lifetime Value</p>
            <p className="text-2xl font-black text-bark">${totalRevenue.toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Modern Filter and Search Bar */}
      <div className="bg-stone-50 p-4 rounded-3xl border border-stone-200 shadow-sm space-y-4 md:space-y-0 md:flex md:items-center md:justify-between gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setActiveFilter('ALL')}
            className={`px-4 py-2.5 rounded-xl font-bold text-xs transition ${
              activeFilter === 'ALL'
                ? 'bg-bark text-white shadow-sm'
                : 'bg-white text-stone-600 hover:bg-stone-100 border border-stone-200'
            }`}
          >
            All Accounts
          </button>
          <button
            onClick={() => setActiveFilter('CORPORATE')}
            className={`px-4 py-2.5 rounded-xl font-bold text-xs transition ${
              activeFilter === 'CORPORATE'
                ? 'bg-bark text-white shadow-sm'
                : 'bg-white text-stone-600 hover:bg-stone-100 border border-stone-200'
            }`}
          >
            Corporate Only
          </button>
          <button
            onClick={() => setActiveFilter('ACTIVE')}
            className={`px-4 py-2.5 rounded-xl font-bold text-xs transition ${
              activeFilter === 'ACTIVE'
                ? 'bg-bark text-white shadow-sm'
                : 'bg-white text-stone-600 hover:bg-stone-100 border border-stone-200'
            }`}
          >
            With Active Jobs
          </button>
          <button
            onClick={() => setActiveFilter('INACTIVE')}
            className={`px-4 py-2.5 rounded-xl font-bold text-xs transition ${
              activeFilter === 'INACTIVE'
                ? 'bg-bark text-white shadow-sm'
                : 'bg-white text-stone-600 hover:bg-stone-100 border border-stone-200'
            }`}
          >
            Inactive / Completed
          </button>
        </div>

        <div className="relative flex-1 max-w-md">
          <input
            type="text"
            placeholder="Search name, email, company, address, notes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-11 pr-4 py-2.5 bg-white border border-stone-200 rounded-xl focus:ring-2 focus:ring-gecko outline-none text-sm shadow-sm transition-all font-medium"
          />
          <div className="absolute left-4 top-3 text-stone-400">
            <Search size={16} />
          </div>
          {searchTerm && (
            <button 
              onClick={() => setSearchTerm('')} 
              className="absolute right-3 top-3 text-stone-400 hover:text-stone-600"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Customer Directory Table */}
      <div className="bg-white rounded-[32px] border border-stone-200 shadow-sm overflow-hidden">
        <div className="hidden md:grid grid-cols-12 px-8 py-4 bg-stone-50 border-b border-stone-200">
          <span className="col-span-3 text-[10px] font-black text-stone-400 uppercase tracking-widest">Client Name / Business</span>
          <span className="col-span-3 text-[10px] font-black text-stone-400 uppercase tracking-widest">Contact Info</span>
          <span className="col-span-3 text-[10px] font-black text-stone-400 uppercase tracking-widest">Service Address</span>
          <span className="col-span-1 text-[10px] font-black text-stone-400 uppercase tracking-widest">LTV</span>
          <span className="col-span-2 text-[10px] font-black text-stone-400 uppercase tracking-widest text-right">Last Activity</span>
        </div>

        <div className="divide-y divide-stone-100">
          {filteredClients.map((client) => (
            <motion.div
              key={client.email}
              whileHover={{ backgroundColor: 'rgba(168, 214, 114, 0.05)' }}
              onClick={() => setSelectedClientEmail(client.email)}
              className="grid grid-cols-1 md:grid-cols-12 px-8 py-6 cursor-pointer transition-colors items-center gap-4 md:gap-0"
            >
              {/* Name & Company Column */}
              <div className="col-span-3 flex items-center space-x-4">
                <div className="w-12 h-12 bg-palm/10 text-palm rounded-2xl flex items-center justify-center font-black text-lg shadow-sm flex-shrink-0">
                  {client.name.charAt(0).toUpperCase()}
                </div>
                <div className="truncate">
                  <span className="block font-bold text-bark text-base truncate">{client.name}</span>
                  {client.companyName ? (
                    <span className="inline-flex items-center gap-1 text-xs text-amber-600 font-bold bg-amber-50 px-2 py-0.5 rounded-full mt-0.5">
                      <Building size={12} />
                      {client.companyName}
                    </span>
                  ) : (
                    <span className="text-xs text-stone-400 font-medium">Individual</span>
                  )}
                </div>
              </div>

              {/* Contact Column */}
              <div className="col-span-3 flex flex-col justify-center">
                <span className="text-sm font-bold text-stone-600 flex items-center gap-1.5">
                  <Mail size={13} className="text-stone-400" />
                  {client.email}
                </span>
                {client.phone && (
                  <span className="text-xs text-stone-400 font-medium flex items-center gap-1.5 mt-0.5">
                    <Phone size={13} className="text-stone-400" />
                    {client.phone}
                  </span>
                )}
              </div>

              {/* Address Column */}
              <div className="col-span-3 text-sm text-stone-500 truncate pr-4 font-medium flex items-center gap-1.5">
                <MapPin size={14} className="text-stone-400 flex-shrink-0" />
                <span className="truncate">{client.address || 'No Address Listed'}</span>
              </div>

              {/* Value Column */}
              <div className="col-span-1 text-sm font-black text-bark">
                ${client.totalRevenue.toLocaleString()}
              </div>

              {/* Activity / Actions Column */}
              <div className="col-span-2 flex justify-end items-center space-x-4 text-right">
                <div>
                  <span className="block text-xs font-black text-gecko uppercase">{client.jobs.length} Jobs</span>
                  <span className="block text-[10px] font-bold text-stone-400 uppercase">
                    {client.lastActivity ? new Date(client.lastActivity).toLocaleDateString() : 'No activity'}
                  </span>
                </div>
                <div className="text-stone-300 w-5 h-5 hidden sm:block">
                  <Icons.Calendar />
                </div>
              </div>
            </motion.div>
          ))}
          
          {filteredClients.length === 0 && (
            <div className="p-20 text-center flex flex-col items-center justify-center">
              <div className="w-20 h-20 bg-stone-50 rounded-3xl flex items-center justify-center mb-6 text-stone-300 transform rotate-12">
                <Icons.Tree />
              </div>
              <p className="text-stone-400 font-black text-xl tracking-tight uppercase">No customers found</p>
              <p className="text-stone-400 mt-2">Try adjusting your search terms or filter selection.</p>
            </div>
          )}
        </div>
      </div>

      {/* Customer Detail Sliding Panel / Dialog */}
      <AnimatePresence>
        {selectedClient && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-bark/40 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-stone-50 w-full max-w-4xl rounded-[40px] shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
            >
              <div className="p-8 bg-bark text-white flex justify-between items-start">
                <div className="flex items-center space-x-6">
                  <div className="w-20 h-20 bg-gecko rounded-3xl flex items-center justify-center text-white text-3xl font-black shadow-lg">
                    {selectedClient.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-3xl font-black">{selectedClient.name}</h3>
                      {selectedClient.companyName && (
                        <span className="inline-flex items-center gap-1.5 text-xs bg-amber-500 text-bark font-black px-3 py-1 rounded-full shadow-sm">
                          <Building size={12} />
                          {selectedClient.companyName}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-3 mt-2">
                      <p className="text-gecko font-bold flex items-center gap-1.5">
                        <Mail size={14} />
                        {selectedClient.email}
                      </p>
                      {selectedClient.phone && (
                        <p className="text-stone-400 font-bold flex items-center gap-1.5">
                          <Phone size={14} />
                          {selectedClient.phone}
                        </p>
                      )}
                      <button 
                        onClick={() => setIsComposingEmail(true)}
                        className="bg-white/20 hover:bg-white/30 text-white text-xs font-bold px-3 py-1 rounded-full transition"
                      >
                        Send Email
                      </button>
                    </div>
                    <p className="text-stone-400 text-sm mt-2 flex items-center gap-1.5">
                      <MapPin size={14} />
                      {selectedClient.address || 'No Address Provided'}
                    </p>
                    {deleteError && (
                      <p role="alert" className="mt-3 text-xs font-bold text-red-100 bg-red-500/25 border border-red-300/30 rounded-lg px-3 py-2">
                        {deleteError}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleOpenEditForm(selectedClient)}
                    className="p-3 bg-white/10 hover:bg-white/20 rounded-2xl transition text-white"
                    title="Edit Customer"
                  >
                    <Edit3 size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(selectedClient)}
                    disabled={isDeleting}
                    className="p-3 bg-red-500/20 hover:bg-red-500/40 rounded-2xl transition text-red-300 disabled:opacity-50"
                    title="Delete Customer"
                  >
                    {isDeleting ? <span className="text-[10px] font-black">…</span> : <Trash2 size={18} />}
                  </button>
                  <button 
                    onClick={() => setSelectedClientEmail(null)}
                    className="p-3 hover:bg-white/10 rounded-2xl transition"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-8 space-y-8 custom-scrollbar">
                
                {/* Client Summary Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white p-6 rounded-3xl border border-stone-200 flex flex-col items-center justify-center text-center shadow-sm">
                    <span className="text-[10px] font-black text-stone-400 uppercase tracking-widest mb-2">Total Value</span>
                    <span className="text-3xl font-black text-bark">${selectedClient.totalRevenue.toLocaleString()}</span>
                  </div>
                  <div className="bg-white p-6 rounded-3xl border border-stone-200 flex flex-col items-center justify-center text-center shadow-sm">
                    <span className="text-[10px] font-black text-stone-400 uppercase tracking-widest mb-2">Total Jobs</span>
                    <span className="text-3xl font-black text-bark">{selectedClient.jobs.length}</span>
                  </div>
                  <div className="bg-white p-6 rounded-3xl border border-stone-200 flex flex-col items-center justify-center text-center shadow-sm">
                    <span className="text-[10px] font-black text-stone-400 uppercase tracking-widest mb-2">Open Leads</span>
                    <span className="text-3xl font-black text-bark">{selectedClient.leads.length}</span>
                  </div>
                </div>

                {/* Specific customer requests or property details (Notes) */}
                <section className="bg-white p-6 rounded-3xl border border-stone-200 shadow-sm">
                  <h4 className="text-[10px] font-black text-stone-400 uppercase tracking-[0.2em] mb-4 flex items-center">
                    <FileText size={14} className="mr-2 text-gecko" />
                    Property Details & Special Requests
                  </h4>
                  {selectedClient.notes ? (
                    <p className="text-stone-700 text-sm leading-relaxed whitespace-pre-wrap bg-stone-50 p-4 rounded-2xl border border-stone-100 font-medium">
                      {selectedClient.notes}
                    </p>
                  ) : (
                    <div className="text-center py-6 bg-stone-50 rounded-2xl border border-dashed border-stone-200">
                      <p className="text-stone-400 text-sm font-bold">No custom property details or requests logged for this account.</p>
                      <button 
                        onClick={() => handleOpenEditForm(selectedClient)}
                        className="text-xs text-gecko font-black mt-2 hover:underline"
                      >
                        + Add Property Notes
                      </button>
                    </div>
                  )}
                </section>

                {selectedClient.address && (
                  <section className="bg-white p-6 rounded-3xl border border-stone-200 shadow-sm">
                    <h4 className="text-[10px] font-black text-stone-400 uppercase tracking-[0.2em] mb-4 flex items-center">
                      <span className="mr-2">📍</span> Primary Location
                    </h4>
                    <MapComponent address={selectedClient.address} title={selectedClient.name} />
                  </section>
                )}

                {selectedClient.jobs.length > 0 && (
                  <section>
                    <h4 className="text-[10px] font-black text-stone-400 uppercase tracking-[0.2em] mb-4">Project History</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {selectedClient.jobs.map(job => (
                        <div key={job.id} className="bg-white p-6 rounded-3xl border border-stone-200 shadow-sm">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <span className="text-[10px] font-black text-gecko uppercase tracking-widest">{job.serviceType}</span>
                              <h5 className="font-bold text-bark truncate max-w-xs">{job.address}</h5>
                            </div>
                            <span className={`text-[10px] font-black px-2 py-1 rounded-full uppercase ${
                              job.status === JobStatus.PAID ? 'bg-gecko/10 text-gecko' : 'bg-orange-100 text-orange-600'
                            }`}>
                              {job.status}
                            </span>
                          </div>
                          
                          {job.documents && job.documents.length > 0 && (
                            <div className="flex gap-2 overflow-x-auto py-2 custom-scrollbar">
                              {job.documents.filter(d => d.url).map(doc => (
                                <img 
                                  key={doc.id} 
                                  src={doc.url} 
                                  alt={doc.title} 
                                  className="h-20 w-32 object-cover rounded-lg border border-stone-200 flex-shrink-0"
                                  referrerPolicy="no-referrer"
                                />
                              ))}
                            </div>
                          )}

                          <div className="flex justify-between items-center mt-4 pt-4 border-t border-stone-100">
                            <span className="text-sm font-bold text-bark">${job.totalPrice.toLocaleString()}</span>
                            <span className="text-xs text-stone-400">{new Date(job.createdAt).toLocaleDateString()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {selectedClient.leads.length > 0 && (
                  <section>
                    <h4 className="text-[10px] font-black text-stone-400 uppercase tracking-[0.2em] mb-4">Open Opportunities</h4>
                    <div className="space-y-3">
                      {selectedClient.leads.map(lead => (
                        <div key={lead.id} className="bg-white p-4 rounded-2xl border border-stone-200 flex justify-between items-center">
                          <div>
                            <span className="text-xs font-bold text-palm">{lead.serviceType}</span>
                            <p className="text-xs text-stone-500">{lead.address}</p>
                          </div>
                          <span className="text-[10px] font-black text-stone-400 uppercase">{new Date(lead.createdAt).toLocaleDateString()}</span>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {selectedClient.jobs.length === 0 && selectedClient.leads.length === 0 && (
                  <div className="text-center py-16 bg-white rounded-3xl border border-dashed border-stone-200">
                    <div className="w-16 h-16 bg-stone-50 rounded-2xl flex items-center justify-center mx-auto mb-4 text-stone-300 transform rotate-12">
                      <Icons.Tree />
                    </div>
                    <p className="text-stone-400 font-bold">No history available for this client.</p>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Gmail Compose Dialog */}
      <AnimatePresence>
        {isComposingEmail && selectedClient && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-bark/40 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden"
            >
              <div className="p-6 border-b border-stone-100 flex justify-between items-center">
                <h3 className="text-xl font-black text-bark">Compose Email</h3>
                <button 
                  onClick={() => setIsComposingEmail(false)}
                  className="p-2 hover:bg-stone-100 rounded-full transition text-stone-400"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-2">To</label>
                  <input 
                    type="text" 
                    value={selectedClient.email} 
                    disabled 
                    className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 text-stone-500 font-medium"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-2">Subject</label>
                  <input 
                    type="text" 
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="e.g., Your upcoming tree service"
                    className="w-full bg-white border border-stone-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-gecko outline-none transition font-medium text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-2">Message</label>
                  <textarea 
                    value={emailBody}
                    onChange={(e) => setEmailBody(e.target.value)}
                    rows={6}
                    placeholder="Write your message here..."
                    className="w-full bg-white border border-stone-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-gecko outline-none transition resize-none custom-scrollbar font-medium text-sm"
                  />
                </div>
              </div>
              <div className="p-6 bg-stone-50 border-t border-stone-100 flex justify-end space-x-3">
                <button 
                  onClick={() => setIsComposingEmail(false)}
                  className="px-6 py-3 font-bold text-stone-500 hover:text-bark transition text-sm"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleSendEmail}
                  disabled={isSending || !emailSubject || !emailBody}
                  className="px-6 py-3 bg-gecko hover:bg-gecko/90 text-white font-bold rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-sm"
                >
                  {isSending ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </>
                  ) : 'Send Email'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Add / Edit Customer Form Modal */}
      <AnimatePresence>
        {isFormOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-bark/40 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              className="bg-white w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden flex flex-col"
            >
              <div className="p-6 border-b border-stone-100 flex justify-between items-center">
                <h3 className="text-xl font-black text-bark">
                  {editingClient ? 'Edit Customer Profile' : 'Add New Customer'}
                </h3>
                <button 
                  onClick={() => setIsFormOpen(false)}
                  className="p-2 hover:bg-stone-100 rounded-full transition text-stone-400"
                >
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4 flex-1 overflow-y-auto custom-scrollbar max-h-[75vh]">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-black text-stone-400 uppercase tracking-wider mb-1">
                      Contact Name *
                    </label>
                    <input 
                      type="text" 
                      required
                      value={formName}
                      onChange={(e) => setFormName(e.target.value)}
                      placeholder="e.g. John Doe"
                      className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-gecko outline-none transition text-sm font-bold text-bark"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-stone-400 uppercase tracking-wider mb-1">
                      Company Name (Optional)
                    </label>
                    <input 
                      type="text" 
                      value={formCompanyName}
                      onChange={(e) => setFormCompanyName(e.target.value)}
                      placeholder="e.g. Green Acres Landscaping"
                      className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-gecko outline-none transition text-sm font-bold text-bark"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-stone-400 uppercase tracking-wider mb-1">
                      This person is a
                    </label>
                    <select
                      value={formPersonType}
                      onChange={(e) => setFormPersonType(e.target.value as any)}
                      className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-gecko outline-none transition text-sm font-bold text-bark"
                    >
                      <option value="CUSTOMER">Customer</option>
                      <option value="EMPLOYEE">Employee</option>
                      <option value="CONTRACTOR">Contractor</option>
                      <option value="OTHER">Other / supplier</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-black text-stone-400 uppercase tracking-wider mb-1">
                      Email Address *
                    </label>
                    <input 
                      type="email" 
                      required
                      value={formEmail}
                      onChange={(e) => setFormEmail(e.target.value)}
                      placeholder="e.g. john@example.com"
                      className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-gecko outline-none transition text-sm font-bold text-bark"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-stone-400 uppercase tracking-wider mb-1">
                      Phone Number
                    </label>
                    <input 
                      type="tel" 
                      value={formPhone}
                      onChange={(e) => setFormPhone(e.target.value)}
                      placeholder="e.g. (555) 019-2834"
                      className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-gecko outline-none transition text-sm font-bold text-bark"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-black text-stone-400 uppercase tracking-wider mb-1">
                    Service Address
                  </label>
                  <input 
                    type="text" 
                    value={formAddress}
                    onChange={(e) => setFormAddress(e.target.value)}
                    placeholder="e.g. 104 Oakwood Lane, Columbus OH"
                    className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-gecko outline-none transition text-sm font-bold text-bark"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-black text-stone-400 uppercase tracking-wider mb-1">
                    Property Notes & Specific Requests
                  </label>
                  <textarea 
                    value={formNotes}
                    onChange={(e) => setFormNotes(e.target.value)}
                    rows={4}
                    placeholder="e.g. Property contains three large White Oak trees in the back garden. Customer requested quotes for crown lifting and safety risk assessments."
                    className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-gecko outline-none transition text-sm font-medium text-bark resize-none"
                  />
                </div>

                <div className="pt-4 border-t border-stone-100 flex justify-end space-x-3">
                  <button 
                    type="button"
                    onClick={() => setIsFormOpen(false)}
                    className="px-5 py-2.5 font-bold text-stone-500 hover:text-bark transition text-xs"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    disabled={isSubmitting}
                    className="px-5 py-2.5 bg-gecko hover:bg-gecko/90 text-white font-black rounded-xl transition disabled:opacity-50 text-xs flex items-center gap-2"
                  >
                    {isSubmitting ? 'Saving...' : editingClient ? 'Save Changes' : 'Create Customer'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ClientsView;
